import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  InputNumber,
  message,
  Popconfirm,
  Typography,
  Statistic,
  Row,
  Col,
  Drawer,
  Progress,
  Tabs,
  Tooltip,
  Checkbox,
  Dropdown,
  Divider,
  Alert
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ToolOutlined,
  PlayCircleOutlined,
  LineChartOutlined,
  ExportOutlined,
  CalculatorOutlined,
  DollarOutlined,
  UndoOutlined,
  ReloadOutlined,
  DownOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { MenuProps } from 'antd';
import dayjs from 'dayjs';
import assetService, {
  Asset,
  AssetCategory,
  DepreciationSchedule,
  PendingDepreciation,
  BatchDepreciationResult
} from '@/services/assetService';
import accountService, { Account } from '@/services/accountService';

const { Title, Text } = Typography;
const { TextArea } = Input;

// Use types from assetService - interfaces are imported above

const FixedAssetManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [categories, setCategories] = useState<AssetCategory[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isCategoryModalVisible, setIsCategoryModalVisible] = useState(false);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [editingCategory, setEditingCategory] = useState<AssetCategory | null>(null);
  const [scheduleDrawerVisible, setScheduleDrawerVisible] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<DepreciationSchedule | null>(null);
  const [form] = Form.useForm();
  const [categoryForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState('assets');

  // New state for enhanced features
  const [batchDepreciationModalVisible, setBatchDepreciationModalVisible] = useState(false);
  const [batchDepreciationForm] = Form.useForm();
  const [batchPreviewResult, setBatchPreviewResult] = useState<BatchDepreciationResult | null>(null);
  const [pendingDepreciations, setPendingDepreciations] = useState<PendingDepreciation[]>([]);
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
  const [batchLoading, setBatchLoading] = useState(false);

  const [reevaluateModalVisible, setReevaluateModalVisible] = useState(false);
  const [reevaluateForm] = Form.useForm();
  const [reevaluatingAsset, setReevaluatingAsset] = useState<Asset | null>(null);

  const [sellModalVisible, setSellModalVisible] = useState(false);
  const [sellForm] = Form.useForm();
  const [sellingAsset, setSellingAsset] = useState<Asset | null>(null);

  const [disposeModalVisible, setDisposeModalVisible] = useState(false);
  const [disposeForm] = Form.useForm();
  const [disposingAsset, setDisposingAsset] = useState<Asset | null>(null);

  const [selectedDepreciationMethod, setSelectedDepreciationMethod] = useState<string>('');
  const [selectedCategoryProrataEnabled, setSelectedCategoryProrataEnabled] = useState(false);

  useEffect(() => {
    fetchAssets();
    fetchCategories();
    fetchAccounts();
  }, []);

  const fetchAssets = async () => {
    try {
      setLoading(true);
      const data = await assetService.getAssets();
      setAssets(data);
    } catch (error: any) {
      message.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await assetService.getCategories();
      setCategories(data);
    } catch (error: any) {
      console.error('Failed to load categories:', error);
    }
  };

  const fetchAccounts = async () => {
    try {
      const data = await accountService.getAccounts({ deprecated: false });
      setAccounts(data);
    } catch (error: any) {
      console.error('Failed to load accounts:', error);
    }
  };

  const fetchPendingDepreciations = async (upToDate?: string) => {
    try {
      const data = await assetService.getPendingDepreciations(upToDate);
      setPendingDepreciations(data);
    } catch (error: any) {
      console.error('Failed to load pending depreciations:', error);
    }
  };

  const getStateColor = (state: string) => {
    const colors: Record<string, string> = {
      DRAFT: 'default',
      RUNNING: 'green',
      DISPOSED: 'orange',
      FULLY_DEPRECIATED: 'blue'
    };
    return colors[state] || 'default';
  };

  const formatCurrency = (amount: string | number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'HKD',
      minimumFractionDigits: 0
    }).format(Number(amount));
  };

  const handleCreate = () => {
    setEditingAsset(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record: Asset) => {
    setEditingAsset(record);
    form.setFieldsValue({
      name: record.name,
      code: record.code,
      categoryId: record.categoryId,
      description: record.description,
      serialNumber: record.serialNumber,
      location: record.location,
      acquisitionDate: dayjs(record.acquisitionDate),
      originalValue: Number(record.originalValue),
      notDepreciableValue: Number(record.notDepreciableValue),
      duration: record.duration,
      depreciationMethod: record.depreciationMethod,
      prorataTemporis: record.prorataTemporis,
      startDepreciatingDate: record.startDepreciatingDate ? dayjs(record.startDepreciatingDate) : undefined
    });
    setIsModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const data = {
        ...values,
        acquisitionDate: values.acquisitionDate ? values.acquisitionDate.toISOString() : undefined,
        startDepreciatingDate: values.startDepreciatingDate ? values.startDepreciatingDate.toISOString() : undefined,
        firstDepreciationDate: values.firstDepreciationDate ? values.firstDepreciationDate.toISOString() : undefined
      };

      if (editingAsset) {
        await assetService.updateAsset(editingAsset.id, data);
        message.success('Asset updated');
      } else {
        await assetService.createAsset(data);
        message.success('Asset created');
      }

      setIsModalVisible(false);
      fetchAssets();
    } catch (error: any) {
      message.error(error.message || 'Operation failed');
    }
  };

  const handleConfirm = async (id: string) => {
    try {
      await assetService.confirmAsset(id);
      message.success('Asset confirmed and depreciation started');
      fetchAssets();
    } catch (error: any) {
      message.error(error.message || 'Failed to confirm asset');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await assetService.deleteAsset(id);
      message.success('Asset deleted');
      fetchAssets();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete asset');
    }
  };

  const handleViewSchedule = async (asset: Asset) => {
    try {
      const data = await assetService.getDepreciationSchedule(asset.id);
      setSelectedSchedule(data);
      setScheduleDrawerVisible(true);
    } catch (error: any) {
      message.error('Failed to load depreciation schedule');
    }
  };

  const handlePostDepreciation = async (assetId: string, date: string) => {
    try {
      await assetService.postDepreciation(assetId, date);
      message.success('Depreciation posted with journal entry');
      // Refresh schedule
      const data = await assetService.getDepreciationSchedule(assetId);
      setSelectedSchedule(data);
      fetchAssets();
    } catch (error: any) {
      message.error(error.message || 'Failed to post depreciation');
    }
  };

  const handleCreateCategory = async () => {
    try {
      const values = await categoryForm.validateFields();

      if (editingCategory) {
        // Update existing category
        await assetService.updateCategory(editingCategory.id, values);
        message.success('Category updated');
      } else {
        // Create new category
        await assetService.createCategory(values);
        message.success('Category created');
      }

      setIsCategoryModalVisible(false);
      categoryForm.resetFields();
      setEditingCategory(null);
      fetchCategories();
    } catch (error: any) {
      message.error(error.message || 'Operation failed');
    }
  };

  const handleEditCategory = (record: AssetCategory) => {
    setEditingCategory(record);
    categoryForm.setFieldsValue({
      code: record.code,
      name: record.name,
      description: record.description,
      depreciationMethod: record.depreciationMethod,
      durationMonths: record.durationMonths,
      accountAssetId: record.accountAssetId,
      accountDepreciationId: record.accountDepreciationId,
      accountExpenseId: record.accountExpenseId,
      accountGainLossId: record.accountGainLossId,
      journalId: record.journalId,
      automateAssets: record.automateAssets || 'NO',
      prorataTemporis: record.prorataTemporis
    });
    setIsCategoryModalVisible(true);
  };

  const handleDeleteCategory = async (id: string) => {
    try {
      await assetService.deleteCategory(id);
      message.success('Category deleted');
      fetchCategories();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete category');
    }
  };

  // Batch Depreciation Handlers
  const handleOpenBatchDepreciation = async () => {
    setBatchDepreciationModalVisible(true);
    batchDepreciationForm.setFieldsValue({
      upToDate: dayjs()
    });
    setBatchPreviewResult(null);
    setSelectedAssetIds([]);
    await fetchPendingDepreciations(dayjs().format('YYYY-MM-DD'));
  };

  const handleBatchPreview = async () => {
    try {
      setBatchLoading(true);
      const values = await batchDepreciationForm.validateFields();
      const result = await assetService.computeBatchDepreciation({
        assetIds: selectedAssetIds.length > 0 ? selectedAssetIds : undefined,
        upToDate: values.upToDate.format('YYYY-MM-DD'),
        previewOnly: true
      });
      setBatchPreviewResult(result);
    } catch (error: any) {
      message.error(error.message || 'Failed to preview depreciation');
    } finally {
      setBatchLoading(false);
    }
  };

  const handleBatchPost = async () => {
    try {
      setBatchLoading(true);
      const values = await batchDepreciationForm.validateFields();
      const result = await assetService.computeBatchDepreciation({
        assetIds: selectedAssetIds.length > 0 ? selectedAssetIds : undefined,
        upToDate: values.upToDate.format('YYYY-MM-DD'),
        previewOnly: false
      });
      message.success(`Posted ${result.posted} depreciation entries with journal entries`);
      setBatchDepreciationModalVisible(false);
      setBatchPreviewResult(null);
      fetchAssets();
    } catch (error: any) {
      message.error(error.message || 'Failed to post depreciation');
    } finally {
      setBatchLoading(false);
    }
  };

  // Reevaluation Handlers
  const handleOpenReevaluate = (asset: Asset) => {
    setReevaluatingAsset(asset);
    reevaluateForm.resetFields();
    reevaluateForm.setFieldsValue({
      newPurchaseValue: Number(asset.purchaseValue),
      newSalvageValue: Number(asset.salvageValue),
      newUsefulLife: asset.usefulLife
    });
    setReevaluateModalVisible(true);
  };

  const handleReevaluate = async () => {
    if (!reevaluatingAsset) return;
    try {
      const values = await reevaluateForm.validateFields();
      await assetService.reevaluateAsset(reevaluatingAsset.id, {
        newPurchaseValue: values.newPurchaseValue,
        newSalvageValue: values.newSalvageValue,
        newUsefulLife: values.newUsefulLife,
        reason: values.reason
      });
      message.success('Asset reevaluated successfully');
      setReevaluateModalVisible(false);
      setReevaluatingAsset(null);
      fetchAssets();
    } catch (error: any) {
      message.error(error.message || 'Failed to reevaluate asset');
    }
  };

  // Sell Asset Handlers
  const handleOpenSell = (asset: Asset) => {
    setSellingAsset(asset);
    sellForm.resetFields();
    sellForm.setFieldsValue({
      saleDate: dayjs(),
      saleValue: Number(asset.bookValue)
    });
    setSellModalVisible(true);
  };

  const handleSell = async () => {
    if (!sellingAsset) return;
    try {
      const values = await sellForm.validateFields();
      await assetService.sellAsset(sellingAsset.id, {
        saleValue: values.saleValue,
        saleDate: values.saleDate.format('YYYY-MM-DD'),
        customerId: values.customerId,
        notes: values.notes
      });
      message.success('Asset sold successfully');
      setSellModalVisible(false);
      setSellingAsset(null);
      fetchAssets();
    } catch (error: any) {
      message.error(error.message || 'Failed to sell asset');
    }
  };

  // Dispose Asset Handlers
  const handleOpenDispose = (asset: Asset) => {
    setDisposingAsset(asset);
    disposeForm.resetFields();
    disposeForm.setFieldsValue({
      disposalDate: dayjs(),
      disposalType: 'SCRAP'
    });
    setDisposeModalVisible(true);
  };

  const handleDispose = async () => {
    if (!disposingAsset) return;
    try {
      const values = await disposeForm.validateFields();
      await assetService.disposeAsset(disposingAsset.id, {
        disposalDate: values.disposalDate.format('YYYY-MM-DD'),
        disposalType: values.disposalType,
        disposalValue: values.disposalValue,
        notes: values.notes
      });
      message.success('Asset disposed successfully');
      setDisposeModalVisible(false);
      setDisposingAsset(null);
      fetchAssets();
    } catch (error: any) {
      message.error(error.message || 'Failed to dispose asset');
    }
  };

  // Undo Disposal Handler
  const handleUndoDisposal = async (assetId: string) => {
    try {
      await assetService.undoDisposal(assetId);
      message.success('Disposal reversed successfully');
      fetchAssets();
    } catch (error: any) {
      message.error(error.message || 'Failed to undo disposal');
    }
  };

  // Handle category change in asset form to check prorata settings
  const handleCategoryChange = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    if (category) {
      setSelectedCategoryProrataEnabled(category.prorataTemporis || false);
      form.setFieldsValue({
        depreciationMethod: category.depreciationMethod,
        usefulLife: category.depreciationYears * 12 // Convert years to months
      });
    }
  };

  // Handle depreciation method change to show/hide decline factor
  const handleDepreciationMethodChange = (method: string) => {
    setSelectedDepreciationMethod(method);
  };

  const columns: ColumnsType<Asset> = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 100
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          {record.description && (
            <Text type="secondary" style={{ fontSize: 12 }}>{record.description}</Text>
          )}
        </Space>
      )
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (cat: AssetCategory) => cat?.name || '-'
    },
    {
      title: 'Acquisition Date',
      dataIndex: 'acquisitionDate',
      key: 'acquisitionDate',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD')
    },
    {
      title: 'Original Value',
      dataIndex: 'originalValue',
      key: 'originalValue',
      align: 'right',
      render: (val: string) => formatCurrency(val)
    },
    {
      title: 'Accum. Depr.',
      dataIndex: 'accumulatedDepreciation',
      key: 'accumulatedDepreciation',
      align: 'right',
      render: (val: string) => formatCurrency(val)
    },
    {
      title: 'Book Value',
      dataIndex: 'bookValue',
      key: 'bookValue',
      align: 'right',
      render: (val: string, record) => (
        <Text style={{ color: Number(val) > 0 ? '#1890ff' : '#f5222d' }}>
          {formatCurrency(val)}
        </Text>
      )
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      render: (state: string) => <Tag color={getStateColor(state)}>{state.replace('_', ' ')}</Tag>
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => {
        // Build actions menu based on asset state
        const getActionMenuItems = (): MenuProps['items'] => {
          const items: MenuProps['items'] = [];

          // Schedule is always available
          items.push({
            key: 'schedule',
            icon: <LineChartOutlined />,
            label: 'View Schedule',
            onClick: () => handleViewSchedule(record)
          });

          if (record.state === 'DRAFT') {
            items.push(
              { type: 'divider' },
              {
                key: 'edit',
                icon: <EditOutlined />,
                label: 'Edit',
                onClick: () => handleEdit(record)
              },
              {
                key: 'confirm',
                icon: <PlayCircleOutlined />,
                label: 'Confirm & Start Depreciation',
                onClick: () => handleConfirm(record.id)
              },
              {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: 'Delete',
                danger: true,
                onClick: () => handleDelete(record.id)
              }
            );
          }

          if (record.state === 'RUNNING') {
            items.push(
              { type: 'divider' },
              {
                key: 'reevaluate',
                icon: <ReloadOutlined />,
                label: 'Reevaluate',
                onClick: () => handleOpenReevaluate(record)
              },
              {
                key: 'sell',
                icon: <DollarOutlined />,
                label: 'Sell Asset',
                onClick: () => handleOpenSell(record)
              },
              {
                key: 'dispose',
                icon: <ExportOutlined />,
                label: 'Dispose (Scrap/Loss)',
                onClick: () => handleOpenDispose(record)
              }
            );
          }

          if (record.state === 'DISPOSED') {
            items.push(
              { type: 'divider' },
              {
                key: 'undo',
                icon: <UndoOutlined />,
                label: 'Undo Disposal',
                onClick: () => handleUndoDisposal(record.id)
              }
            );
          }

          return items;
        };

        return (
          <Dropdown menu={{ items: getActionMenuItems() }} trigger={['click']}>
            <Button>
              Actions <DownOutlined />
            </Button>
          </Dropdown>
        );
      }
    }
  ];

  const categoryColumns: ColumnsType<AssetCategory> = [
    { title: 'Code', dataIndex: 'code', key: 'code' },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Method',
      dataIndex: 'depreciationMethod',
      key: 'depreciationMethod',
      render: (method: string) => method.replace(/_/g, ' ')
    },
    { title: 'Duration (Months)', dataIndex: 'durationMonths', key: 'durationMonths' },
    {
      title: 'Journal',
      dataIndex: 'journal',
      key: 'journal',
      render: (journal: { name: string } | undefined) => journal?.name || '-'
    },
    {
      title: 'Prorata',
      dataIndex: 'prorataTemporis',
      key: 'prorataTemporis',
      render: (prorata: boolean) => prorata ? <Tag color="blue">Yes</Tag> : <Tag>No</Tag>
    },
    {
      title: 'Auto-create',
      dataIndex: 'automateAssets',
      key: 'automateAssets',
      render: (auto: string) => {
        const labels: Record<string, string> = {
          'NO': 'No',
          'CREATE_DRAFT': 'Draft',
          'CREATE_VALIDATE': 'Validated'
        };
        return labels[auto] || auto;
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: AssetCategory) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditCategory(record)}
            size="small"
          />
          <Popconfirm
            title="Delete this category?"
            onConfirm={() => handleDeleteCategory(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const scheduleColumns: ColumnsType<DepreciationSchedule['schedule'][0]> = [
    {
      title: '#',
      dataIndex: 'sequence',
      key: 'sequence',
      width: 50,
      render: (seq: number) => seq || '-'
    },
    { title: 'Period', dataIndex: 'period', key: 'period', width: 80 },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date: string) => dayjs(date).format('MMM YYYY')
    },
    {
      title: 'Depreciation',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: (val: number) => formatCurrency(val)
    },
    {
      title: 'Accumulated',
      dataIndex: 'accumulatedAmount',
      key: 'accumulatedAmount',
      align: 'right',
      render: (val: number) => formatCurrency(val)
    },
    {
      title: 'Book Value',
      dataIndex: 'bookValue',
      key: 'bookValue',
      align: 'right',
      render: (val: number) => formatCurrency(val)
    },
    {
      title: 'Remaining',
      dataIndex: 'remainingValue',
      key: 'remainingValue',
      align: 'right',
      render: (val: number) => formatCurrency(val)
    },
    {
      title: 'Status',
      dataIndex: 'posted',
      key: 'posted',
      render: (posted: boolean, record: any) => (
        <Space>
          <Tag color={posted ? 'green' : 'default'} icon={posted ? <CheckCircleOutlined /> : <ClockCircleOutlined />}>
            {posted ? 'Posted' : 'Pending'}
          </Tag>
          {posted && record.journalEntryId && (
            <Tooltip title="View Journal Entry">
              <Button type="link" size="small" style={{ padding: 0 }}>
                JE
              </Button>
            </Tooltip>
          )}
        </Space>
      )
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        !record.posted && selectedSchedule && (
          <Popconfirm
            title="Post this depreciation?"
            description="This will create a journal entry."
            onConfirm={() => handlePostDepreciation(selectedSchedule.assetId, record.date)}
          >
            <Button size="small" type="primary">
              Post
            </Button>
          </Popconfirm>
        )
      )
    }
  ];

  // Calculate summary stats
  const totalPurchaseValue = assets.reduce((sum, a) => sum + Number(a.purchaseValue), 0);
  const totalAccumDepr = assets.reduce((sum, a) => sum + Number(a.accumulatedDepreciation), 0);
  const totalBookValue = assets.reduce((sum, a) => sum + Number(a.bookValue), 0);

  return (
    <>
      {/* Summary Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Assets"
              value={assets.length}
              prefix={<ToolOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Purchase Value"
              value={totalPurchaseValue}
              formatter={(v) => formatCurrency(Number(v))}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Accumulated Depreciation"
              value={totalAccumDepr}
              formatter={(v) => formatCurrency(Number(v))}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Book Value"
              value={totalBookValue}
              formatter={(v) => formatCurrency(Number(v))}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'assets',
              label: 'Assets',
              children: (
                <Table
                  columns={columns}
                  dataSource={assets}
                  rowKey="id"
                  loading={loading}
                  pagination={{ pageSize: 10 }}
                />
              )
            },
            {
              key: 'categories',
              label: 'Categories',
              children: (
                <Table
                  columns={categoryColumns}
                  dataSource={categories}
                  rowKey="id"
                  pagination={false}
                />
              )
            }
          ]}
          tabBarExtraContent={
            <Space>
              {activeTab === 'assets' && (
                <>
                  <Button
                    icon={<CalculatorOutlined />}
                    onClick={handleOpenBatchDepreciation}
                  >
                    Batch Depreciation
                  </Button>
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                    New Asset
                  </Button>
                </>
              )}
              {activeTab === 'categories' && (
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsCategoryModalVisible(true)}>
                  New Category
                </Button>
              )}
            </Space>
          }
        />
      </Card>

      {/* Asset Form Modal */}
      <Modal
        title={editingAsset ? 'Edit Asset' : 'New Asset'}
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={() => setIsModalVisible(false)}
        width={700}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="code" label="Asset Code" rules={[{ required: true }]}>
                <Input placeholder="e.g., FA-001" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="name" label="Asset Name" rules={[{ required: true }]}>
                <Input placeholder="e.g., Dell Computer" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="categoryId" label="Category" rules={[{ required: true }]}>
                <Select placeholder="Select category" onChange={handleCategoryChange}>
                  {categories.map(cat => (
                    <Select.Option key={cat.id} value={cat.id}>{cat.name}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="acquisitionDate" label="Acquisition Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="originalValue" label="Original Value" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} prefix="$" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="notDepreciableValue" label="Not Depreciable Value">
                <InputNumber style={{ width: '100%' }} min={0} prefix="$" defaultValue={0} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="duration" label="Duration (Months)" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="depreciationMethod" label="Depreciation Method">
                <Select onChange={handleDepreciationMethodChange}>
                  <Select.Option value="STRAIGHT_LINE">Straight Line</Select.Option>
                  <Select.Option value="DECLINING_BALANCE">Declining Balance</Select.Option>
                  <Select.Option value="SUM_OF_YEARS">Sum of Years</Select.Option>
                  <Select.Option value="DECLINING_THEN_STRAIGHT_LINE">Declining then Straight Line</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              {(selectedDepreciationMethod === 'DECLINING_BALANCE' || selectedDepreciationMethod === 'DECLINING_THEN_STRAIGHT_LINE') && (
                <Form.Item name="declineFactor" label="Decline Factor" tooltip="Multiplier for declining balance (e.g., 2.0 for double declining)">
                  <InputNumber style={{ width: '100%' }} min={1} max={3} step={0.1} defaultValue={2} />
                </Form.Item>
              )}
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="location" label="Location">
                <Input placeholder="e.g., Office Building A" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="serialNumber" label="Serial Number">
                <Input placeholder="Optional" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="prorataTemporis" valuePropName="checked">
                <Checkbox>Prorata Temporis</Checkbox>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="startDepreciatingDate" label="Start Depreciating">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Description">
            <TextArea rows={2} placeholder="Optional description" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Category Form Modal */}
      <Modal
        title={editingCategory ? 'Edit Asset Category' : 'New Asset Category'}
        open={isCategoryModalVisible}
        onOk={handleCreateCategory}
        onCancel={() => {
          setIsCategoryModalVisible(false);
          categoryForm.resetFields();
          setEditingCategory(null);
        }}
        width={600}
      >
        <Form form={categoryForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="code" label="Category Code" rules={[{ required: true }]}>
                <Input placeholder="e.g., COMP" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="name" label="Category Name" rules={[{ required: true }]}>
                <Input placeholder="e.g., Computer Equipment" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="depreciationMethod" label="Depreciation Method" rules={[{ required: true }]}>
                <Select>
                  <Select.Option value="STRAIGHT_LINE">Straight Line</Select.Option>
                  <Select.Option value="DECLINING_BALANCE">Declining Balance</Select.Option>
                  <Select.Option value="SUM_OF_YEARS">Sum of Years</Select.Option>
                  <Select.Option value="DECLINING_THEN_STRAIGHT_LINE">Declining then Straight Line</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="durationMonths" label="Duration (Months)" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={1} defaultValue={36} />
              </Form.Item>
            </Col>
          </Row>
          <Divider orientation="left">Accounting Configuration</Divider>
          <Alert
            type="warning"
            message="Required for Depreciation Posting"
            description="You must configure expense and depreciation accounts to post depreciation journal entries."
            style={{ marginBottom: 16 }}
          />
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="accountExpenseId" label="Depreciation Expense Account" rules={[{ required: true, message: 'Required for posting depreciation' }]}>
                <Select
                  showSearch
                  placeholder="Select expense account"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={accounts
                    .filter(acc => acc.accountType === 'EXPENSE')
                    .map(acc => ({
                      value: acc.id,
                      label: `${acc.code} - ${acc.name}`
                    }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="accountDepreciationId" label="Accumulated Depreciation Account" rules={[{ required: true, message: 'Required for posting depreciation' }]}>
                <Select
                  showSearch
                  placeholder="Select depreciation account"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={accounts
                    .filter(acc => acc.accountType === 'ASSET')
                    .map(acc => ({
                      value: acc.id,
                      label: `${acc.code} - ${acc.name}`
                    }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="accountAssetId" label="Fixed Asset Account">
                <Select
                  showSearch
                  allowClear
                  placeholder="Optional: Select asset account"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={accounts
                    .filter(acc => acc.accountType === 'ASSET')
                    .map(acc => ({
                      value: acc.id,
                      label: `${acc.code} - ${acc.name}`
                    }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="accountGainLossId" label="Gain/Loss Account">
                <Select
                  showSearch
                  allowClear
                  placeholder="Optional: Select gain/loss account"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={accounts.map(acc => ({
                    value: acc.id,
                    label: `${acc.code} - ${acc.name}`
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="journalId" label="Journal">
            <Input placeholder="Optional: Journal ID for depreciation entries" />
          </Form.Item>
          <Divider orientation="left">Additional Settings</Divider>
          <Form.Item name="description" label="Description">
            <TextArea rows={2} placeholder="Optional description" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="automateAssets" label="Auto-create Assets" tooltip="Automatically create assets from vendor bills">
                <Select defaultValue="NO">
                  <Select.Option value="NO">No</Select.Option>
                  <Select.Option value="CREATE_DRAFT">Create as Draft</Select.Option>
                  <Select.Option value="CREATE_VALIDATE">Create and Validate</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="prorataTemporis" label="Prorata Temporis" valuePropName="checked" tooltip="Calculate partial depreciation for first period">
                <Checkbox>Enable prorata calculation</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Depreciation Schedule Drawer */}
      <Drawer
        title={`Depreciation Schedule: ${selectedSchedule?.assetName || ''}`}
        placement="right"
        width={800}
        open={scheduleDrawerVisible}
        onClose={() => setScheduleDrawerVisible(false)}
      >
        {selectedSchedule && (
          <>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="Original Value"
                    value={selectedSchedule.originalValue}
                    formatter={(v) => formatCurrency(Number(v))}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="Not Depreciable"
                    value={selectedSchedule.notDepreciableValue}
                    formatter={(v) => formatCurrency(Number(v))}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="Depreciable Value"
                    value={selectedSchedule.depreciableValue}
                    formatter={(v) => formatCurrency(Number(v))}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="Duration"
                    value={selectedSchedule.duration}
                    suffix="months"
                  />
                </Card>
              </Col>
            </Row>
            <Text type="secondary" style={{ marginBottom: 16, display: 'block' }}>
              Method: {selectedSchedule.depreciationMethod.replace('_', ' ')}
            </Text>
            <Table
              columns={scheduleColumns}
              dataSource={selectedSchedule.schedule}
              rowKey="period"
              pagination={{ pageSize: 12 }}
              size="small"
            />
          </>
        )}
      </Drawer>

      {/* Batch Depreciation Modal */}
      <Modal
        title="Batch Depreciation Computation"
        open={batchDepreciationModalVisible}
        onCancel={() => {
          setBatchDepreciationModalVisible(false);
          setBatchPreviewResult(null);
        }}
        width={900}
        footer={[
          <Button key="cancel" onClick={() => setBatchDepreciationModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="preview" onClick={handleBatchPreview} loading={batchLoading}>
            Preview
          </Button>,
          <Button
            key="post"
            type="primary"
            onClick={handleBatchPost}
            loading={batchLoading}
            disabled={!batchPreviewResult || batchPreviewResult.preview.length === 0}
          >
            Post All
          </Button>
        ]}
      >
        <Form form={batchDepreciationForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="upToDate" label="Compute Depreciation Up To" rules={[{ required: true }]}>
                <DatePicker
                  style={{ width: '100%' }}
                  onChange={(date) => {
                    if (date) {
                      fetchPendingDepreciations(date.format('YYYY-MM-DD'));
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Select Assets (leave empty for all)">
                <Select
                  mode="multiple"
                  placeholder="All running assets"
                  value={selectedAssetIds}
                  onChange={setSelectedAssetIds}
                  allowClear
                  style={{ width: '100%' }}
                >
                  {assets.filter(a => a.state === 'RUNNING').map(asset => (
                    <Select.Option key={asset.id} value={asset.id}>
                      {asset.code} - {asset.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>

        {pendingDepreciations.length > 0 && !batchPreviewResult && (
          <Alert
            type="info"
            message={`${pendingDepreciations.length} assets have pending depreciations`}
            description={
              <Text type="secondary">
                Total pending amount: {formatCurrency(pendingDepreciations.reduce((sum, p) => sum + p.pendingAmount, 0))}
              </Text>
            }
            style={{ marginBottom: 16 }}
          />
        )}

        {batchPreviewResult && (
          <>
            <Divider orientation="left">Preview Results</Divider>
            <Alert
              type="success"
              message={`${batchPreviewResult.preview.length} depreciation entries to be posted`}
              style={{ marginBottom: 16 }}
            />
            <Table
              dataSource={batchPreviewResult.preview}
              rowKey={(r) => `${r.assetId}-${r.sequence}`}
              size="small"
              pagination={{ pageSize: 10 }}
              columns={[
                { title: 'Asset Code', dataIndex: 'assetCode', key: 'assetCode' },
                { title: 'Asset Name', dataIndex: 'assetName', key: 'assetName' },
                { title: 'Date', dataIndex: 'date', key: 'date', render: (d: string) => dayjs(d).format('YYYY-MM-DD') },
                { title: 'Amount', dataIndex: 'amount', key: 'amount', align: 'right', render: (v: number) => formatCurrency(v) },
                { title: 'Seq #', dataIndex: 'sequence', key: 'sequence' }
              ]}
              summary={(data) => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}><strong>Total</strong></Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <strong>{formatCurrency(data.reduce((sum, r) => sum + r.amount, 0))}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} />
                </Table.Summary.Row>
              )}
            />
          </>
        )}
      </Modal>

      {/* Reevaluation Modal */}
      <Modal
        title={`Reevaluate Asset: ${reevaluatingAsset?.name || ''}`}
        open={reevaluateModalVisible}
        onOk={handleReevaluate}
        onCancel={() => {
          setReevaluateModalVisible(false);
          setReevaluatingAsset(null);
        }}
        width={500}
      >
        {reevaluatingAsset && (
          <>
            <Alert
              type="info"
              message="Asset reevaluation will recalculate the depreciation schedule"
              description="Pending depreciation entries will be regenerated based on new values."
              style={{ marginBottom: 16 }}
            />
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <Statistic title="Current Original Value" value={Number(reevaluatingAsset.originalValue)} prefix="$" />
              </Col>
              <Col span={8}>
                <Statistic title="Current Not Depreciable" value={Number(reevaluatingAsset.notDepreciableValue)} prefix="$" />
              </Col>
              <Col span={8}>
                <Statistic title="Current Duration" value={reevaluatingAsset.duration} suffix="months" />
              </Col>
            </Row>
            <Divider />
            <Form form={reevaluateForm} layout="vertical">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="newOriginalValue" label="New Original Value">
                    <InputNumber style={{ width: '100%' }} min={0} prefix="$" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="newNotDepreciableValue" label="New Not Depreciable Value">
                    <InputNumber style={{ width: '100%' }} min={0} prefix="$" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="newDuration" label="New Duration (Months)">
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
              <Form.Item name="reason" label="Reason for Reevaluation" rules={[{ required: true }]}>
                <TextArea rows={3} placeholder="Describe the reason for this reevaluation" />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {/* Sell Asset Modal */}
      <Modal
        title={`Sell Asset: ${sellingAsset?.name || ''}`}
        open={sellModalVisible}
        onOk={handleSell}
        onCancel={() => {
          setSellModalVisible(false);
          setSellingAsset(null);
        }}
        width={500}
      >
        {sellingAsset && (
          <>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Statistic title="Book Value" value={Number(sellingAsset.bookValue)} prefix="$" />
              </Col>
              <Col span={12}>
                <Statistic title="Accum. Depreciation" value={Number(sellingAsset.accumulatedDepreciation)} prefix="$" />
              </Col>
            </Row>
            <Form form={sellForm} layout="vertical">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="saleValue" label="Sale Value" rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} min={0} prefix="$" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="saleDate" label="Sale Date" rules={[{ required: true }]}>
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="customerId" label="Customer" rules={[{ required: true }]}>
                <Input placeholder="Customer ID (for invoice)" />
              </Form.Item>
              <Form.Item name="notes" label="Notes">
                <TextArea rows={2} placeholder="Optional notes about the sale" />
              </Form.Item>
            </Form>
            <Alert
              type="info"
              message={`Gain/Loss will be calculated: Sale Value - Book Value (${formatCurrency(Number(sellingAsset.bookValue))})`}
              style={{ marginTop: 16 }}
            />
          </>
        )}
      </Modal>

      {/* Dispose Asset Modal */}
      <Modal
        title={`Dispose Asset: ${disposingAsset?.name || ''}`}
        open={disposeModalVisible}
        onOk={handleDispose}
        onCancel={() => {
          setDisposeModalVisible(false);
          setDisposingAsset(null);
        }}
        width={500}
      >
        {disposingAsset && (
          <>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Statistic title="Book Value" value={Number(disposingAsset.bookValue)} prefix="$" />
              </Col>
              <Col span={12}>
                <Statistic title="Accum. Depreciation" value={Number(disposingAsset.accumulatedDepreciation)} prefix="$" />
              </Col>
            </Row>
            <Form form={disposeForm} layout="vertical">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="disposalType" label="Disposal Type" rules={[{ required: true }]}>
                    <Select>
                      <Select.Option value="SCRAP">Scrap</Select.Option>
                      <Select.Option value="DONATION">Donation</Select.Option>
                      <Select.Option value="LOSS">Loss/Theft</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="disposalDate" label="Disposal Date" rules={[{ required: true }]}>
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="disposalValue" label="Disposal Value (if any)">
                <InputNumber style={{ width: '100%' }} min={0} prefix="$" />
              </Form.Item>
              <Form.Item name="notes" label="Notes">
                <TextArea rows={2} placeholder="Notes about disposal reason" />
              </Form.Item>
            </Form>
            <Alert
              type="warning"
              message="This will mark the asset as disposed and create a journal entry recording the loss."
              style={{ marginTop: 16 }}
            />
          </>
        )}
      </Modal>
    </>
  );
};

export default FixedAssetManagement;
